// src/pages/Settings.tsx
import React, { useState } from 'react';
import { Users, Bell, Shield, Database, Network, MapPin, Building2, Monitor } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LocationTypeManager from '../components/LocationTypeManager';
import UserManagement from '../components/UserManagement';
import CompanyManagement from '../components/CompanyManagement';
import DefaultContentManager from '../components/DefaultContentManager';
import StorageManagement from '../components/StorageManagement';
import DeviceMap from '../components/DeviceMap';

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [showLocationTypeManager, setShowLocationTypeManager] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showCompanyManagement, setShowCompanyManagement] = useState(false);
  const [showDefaultContentManager, setShowDefaultContentManager] = useState(false);
  const [showStorageManagement, setShowStorageManagement] = useState(false);
  const [showDeviceMap, setShowDeviceMap] = useState(false);

  const isSuperAdmin = user?.role === 'SuperAdmin';

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Settings</h1>
          <p className="text-gray-600">Manage system preferences and configuration</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Default Content */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <Monitor className="w-5 h-5 text-blue-500 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Default Content</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Set fallback content when no schedules are active
          </p>
          <button 
            onClick={() => setShowDefaultContentManager(true)}
            className="text-blue-600 text-sm hover:text-blue-700"
          >
            Manage Default Content →
          </button>
        </div>

        {/* User Management */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <Users className="w-5 h-5 text-blue-500 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">User Management</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Manage user accounts, roles, and permissions
          </p>
          <button 
            onClick={() => setShowUserManagement(true)}
            className="text-blue-600 text-sm hover:text-blue-700"
          >
            Manage Users →
          </button>
        </div>

        {/* Company Management - Only for SuperAdmin */}
        {isSuperAdmin && (
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center mb-4">
              <Building2 className="w-5 h-5 text-blue-500 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Company Management</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Manage customer companies and approvals
            </p>
            <button 
              onClick={() => setShowCompanyManagement(true)}
              className="text-blue-600 text-sm hover:text-blue-700"
            >
              Manage Companies →
            </button>
          </div>
        )}

        {/* Location Types */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <MapPin className="w-5 h-5 text-blue-500 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Location Types</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Manage location categories and types
          </p>
          <button 
            onClick={() => setShowLocationTypeManager(true)}
            className="text-blue-600 text-sm hover:text-blue-700"
          >
            Manage Types →
          </button>
        </div>

        {/* Storage */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <Database className="w-5 h-5 text-blue-500 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Storage</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            View storage usage and file information
          </p>
          <button 
            onClick={() => setShowStorageManagement(true)}
            className="text-blue-600 text-sm hover:text-blue-700"
          >
            View Storage →
          </button>
        </div>

        {/* Network Map - NEW */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <Network className="w-5 h-5 text-blue-500 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Network Map</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            View device locations on an interactive map
          </p>
          <button 
            onClick={() => setShowDeviceMap(true)}
            className="text-blue-600 text-sm hover:text-blue-700"
          >
            View Map →
          </button>
        </div>

      </div>

      <div className="mt-6 bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-medium text-gray-900 mb-4">System Information</h2>
        <div className="space-y-2">
          <p className="text-sm text-gray-600">
            <span className="font-medium">Version:</span> 1.1.0
          </p>
          
          <p className="text-sm text-gray-600">
            <span className="font-medium">Last Updated:</span> {new Date().toLocaleDateString()}
          </p>
          <p className="text-sm text-gray-600">
            <span className="font-medium">Environment:</span> Production
          </p>
          {user && (
            <>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Logged in as:</span> {user.firstName} {user.lastName} ({user.role})
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Company:</span> {user.companyName}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Location Type Manager Modal */}
      {showLocationTypeManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-2xl">
            <LocationTypeManager onClose={() => setShowLocationTypeManager(false)} />
          </div>
        </div>
      )}

      {/* User Management Modal */}
      {showUserManagement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-4xl">
            <UserManagement onClose={() => setShowUserManagement(false)} />
          </div>
        </div>
      )}

      {/* Company Management Modal */}
      {showCompanyManagement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-6xl">
            <CompanyManagement onClose={() => setShowCompanyManagement(false)} />
          </div>
        </div>
      )}

      {/* Default Content Manager Modal */}
      {showDefaultContentManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-4xl">
            <DefaultContentManager onClose={() => setShowDefaultContentManager(false)} />
          </div>
        </div>
      )}

      {/* Storage Management Modal */}
      {showStorageManagement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-6xl max-h-[90vh] overflow-auto">
            <StorageManagement onClose={() => setShowStorageManagement(false)} />
          </div>
        </div>
      )}

      {/* Device Map Modal - NEW */}
      {showDeviceMap && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-6xl max-h-[90vh]">
            <DeviceMap onClose={() => setShowDeviceMap(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
